import { useState, type FunctionComponent } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@heroicons/react/24/solid/CheckCircleIcon';
import { usePagination } from '@starjack/shared-components';
import {
    PAGE_TEXT_MAX_LENGTH,
    useGetAssistantPagesQuery,
    usePutAssistantPageMutation,
    type PageKnowledgeEntry,
} from '@starjack/entities/assistantConfig';
import { ASSISTANT_API } from '@starjack/utils';
import {
    StarjackTable,
    type StajackTableColumn,
} from '@/components/Table/v2/Table';
import {
    errorStatus,
    isConnectionError,
    MISCONFIGURED_MESSAGE,
} from '@/pageContents/kiEinstellungen/assistantApiError';

export const AssistantPagesContent: FunctionComponent = () => {
    const { data, isLoading, error } = useGetAssistantPagesQuery(undefined, {
        skip: !ASSISTANT_API,
    });
    const [save, { isLoading: isSaving, error: saveError, reset: resetSave }] =
        usePutAssistantPageMutation();
    const [editing, setEditing] = useState<PageKnowledgeEntry | null>(null);
    const [draft, setDraft] = useState('');

    const pages = data ?? [];
    const {
        currentItems,
        countTotal,
        page,
        setNextPage,
        itemsPerPage,
        setItemsPerPage,
    } = usePagination({ items: pages, itemsPerPage: 25 });

    if (!ASSISTANT_API)
        return <Alert severity='error'>{MISCONFIGURED_MESSAGE}</Alert>;

    if (isLoading) return <CircularProgress />;
    if (error)
        return (
            <Alert severity='error'>
                {isConnectionError(error)
                    ? 'Assistant nicht erreichbar'
                    : 'Seiten nicht ladbar'}
            </Alert>
        );

    const columns: StajackTableColumn<PageKnowledgeEntry>[] = [
        { label: 'Seite', itemKey: 'title' },
        { label: 'Route', itemKey: 'route' },
        {
            label: 'Text',
            itemTransform: (row) =>
                row.hasText ? (
                    <SvgIcon fontSize='small' color='success'>
                        <CheckCircleIcon />
                    </SvgIcon>
                ) : null,
            options: {
                headerCellProps: { align: 'center' },
                rowCellProps: { align: 'center' },
            },
        },
    ];

    const open = (row: PageKnowledgeEntry) => {
        // Clear a stale result from a previous row's save — RTK Query keeps
        // the last mutation outcome until the trigger runs again or is reset,
        // so without this a failed save on row A would render its error alert
        // on row B too, before row B's save was ever attempted.
        resetSave();
        setEditing(row);
        setDraft(row.text ?? '');
    };

    const close = () => {
        resetSave();
        setEditing(null);
    };

    const submit = async () => {
        if (!editing) return;
        try {
            await save({ slug: editing.slug, text: draft }).unwrap();
            close();
        } catch {
            // saveError from the mutation hook drives the dialog's inline
            // alert; keep the dialog open so the note isn't lost.
        }
    };

    return (
        <>
            <StarjackTable<PageKnowledgeEntry>
                allItems={pages}
                count={countTotal}
                currentPage={page}
                items={currentItems}
                onPageChange={setNextPage}
                rowsPerPage={itemsPerPage}
                onRowsPerPageChange={setItemsPerPage}
                itemId='slug'
                columns={columns}
                onRowClick={open}
            />

            <Dialog
                open={editing !== null}
                onClose={close}
                fullWidth
                maxWidth='md'
            >
                <DialogTitle>{editing?.title}</DialogTitle>
                <DialogContent>
                    <Typography variant='body2' sx={{ mb: 2 }}>
                        Diese Information erhält der Assistent, sobald sich ein
                        Kunde auf dieser Seite befindet.
                    </Typography>
                    <TextField
                        autoFocus
                        multiline
                        minRows={6}
                        fullWidth
                        // Empty text is how a page is cleared — see the service.
                        helperText={`Leer lassen entfernt den Text. Max. ${PAGE_TEXT_MAX_LENGTH} Zeichen.`}
                        slotProps={{
                            htmlInput: { maxLength: PAGE_TEXT_MAX_LENGTH },
                        }}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                    />
                    {saveError && (
                        <Alert severity='error' sx={{ mt: 2 }}>
                            {errorStatus(saveError) === 400
                                ? 'Speichern fehlgeschlagen: der Text überschreitet die erlaubte Länge oder die Seite ist unbekannt.'
                                : 'Speichern fehlgeschlagen.'}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Abbrechen</Button>
                    <Button
                        variant='contained'
                        disabled={isSaving}
                        onClick={() => void submit()}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
